import { createClient } from '@/shared/api';
import UPDATE_FACILITY_NOTE_MUTATION from '@/entities/facility/api/mutations/updateFacilityNote';
import type {
  UpdateFacilityNoteMutation,
  UpdateFacilityNoteMutationVariables,
} from '@/shared/api/generated/graphql';

export type UpdateFacilityNoteResult = {
  isSuccess: boolean;
  message: string;
  facility: { facilityNote: string };
};

/**
 * Saves the free-text note attached to a facility. Runs the update mutation, throws the server message on failure, and returns the stored facilityNote.
 *
 * @param id - Identifier of the facility.
 * @param note - New note text; omitted to clear it.
 * @returns A promise resolving to the saved facility note.
 */
const handleUpdateFacilityNote = async (id: string, note?: string) => {
  const client = await createClient();
  const updateFacilityNoteResult = await client.mutate<
    UpdateFacilityNoteMutation,
    UpdateFacilityNoteMutationVariables
  >({
    mutation: UPDATE_FACILITY_NOTE_MUTATION,
    variables: {
      id,
      note,
    },
  });
  const { data } = updateFacilityNoteResult;
  const { updateFacilityNote } = data;
  if (!updateFacilityNote.isSuccess) {
    throw new Error(updateFacilityNote.message);
  }
  const { facility } = updateFacilityNote;
  return facility.facilityNote;
};

export default handleUpdateFacilityNote;
